import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import useStyles from "./styles";
import classNames from "classnames";
import { CustomTypography } from '../../components/CustomTypography/CustomTypography';
import { colors } from '../../styles/colors';
import { CustomButton } from '../../components/CustomButton/CustomButton';
import { Sidebar } from "../../containers/Sidebar/Sidebar";
import { useTranslation } from "react-i18next";
import { useVehicleTracking } from '../../hooks/useVehicleTracking';
import { useWebSocket } from '../../hooks/useWebSocket';
import { VehiclePosition } from '../../types/websocket';
import { getCurrentFleetId } from '../../utils/getCurrentFleet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface EnhancedVehicle extends VehiclePosition {
  immatriculation: string;
  brand?: string;
  model?: string;
  displayStatus: 'active' | 'inactive';
}

type StatusFilter = 'all' | 'active' | 'inactive';

/**
 * Escape a value before it goes into map-popup HTML.
 *
 * The popup is assembled as an HTML string and handed to Leaflet's
 * bindPopup(), which injects it with innerHTML. Registration, brand and model
 * arrive over the WebSocket from vehicle records, so interpolating them raw
 * meant any markup stored in those fields executed in the operator's session.
 * Fleet data is edited by users and imported from external telematics, so it
 * is not trusted input.
 */
const escapeHtml = (value: unknown): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

export const Geolocalisation: React.FC = () => {
  const [map, setMap] = useState<L.Map | null>(null);
  const markersRef = useRef<globalThis.Map<string, L.Marker>>(new globalThis.Map());
  const [selectedVin, setSelectedVin] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const mapRef = useRef<HTMLDivElement>(null);
  const [isConnecting, setIsConnecting] = useState(false);

  // State, not a ref: this drives the status text, and a ref mutation does not
  // re-render — so "connection failed" was computed but never actually shown.
  const [failedAttempts, setFailedAttempts] = useState(0);
  const maxReconnectAttempts = 5;

  const classes = useStyles();
  const { t } = useTranslation();

  const { connect, isConnected, disconnect } = useWebSocket();
  const { vehicles, vehicleStatuses, alerts } = useVehicleTracking(getCurrentFleetId());

  const getBatteryColor = useCallback((level?: number): string => {
    if (level == null) return '#9ca3af';
    if (level > 50) return colors.success;
    if (level > 25) return '#f59e0b';
    return colors.error;
  }, []);

  const getFuelColor = useCallback((level?: number): string => {
    if (level == null) return '#9ca3af';
    if (level > 25) return colors.success;
    if (level > 10) return '#f59e0b';
    return colors.error;
  }, []);

  const determineDisplayStatus = useCallback(
    (vehicle: VehiclePosition, status: any): 'active' | 'inactive' => {
      const lastUpdateTime = new Date(vehicle.timestamp).getTime();
      if (lastUpdateTime < Date.now() - 10 * 60 * 1000) return 'inactive';
      if (vehicle.ignition === false) return 'inactive';
      if (vehicle.speed > 5) return 'active';

      switch (status?.status) {
        case 'driving':
        case 'idle':
          return 'active';
        case 'parked':
        case 'maintenance':
          return 'inactive';
        default:
          return 'active';
      }
    },
    []
  );

  const enhancedVehicles: EnhancedVehicle[] = useMemo(() => {
    if (!vehicles || vehicles.length === 0) return [];

    return (vehicles as VehiclePosition[])
      // 0,0 is the null island: a device reporting no GPS fix, not a vehicle
      // in the Atlantic.
      .filter((v) => v.latitude !== 0 && v.longitude !== 0)
      .map((vehicle) => {
        const status = vehicleStatuses[vehicle.vin] || {};
        return {
          ...vehicle,
          immatriculation: vehicle.vehicle_metadata?.immatriculation || vehicle.vin,
          brand: vehicle.vehicle_metadata?.brand || status.brand || '—',
          model: vehicle.vehicle_metadata?.model || status.model || '',
          displayStatus: determineDisplayStatus(vehicle, status),
        };
      });
  }, [vehicles, vehicleStatuses, determineDisplayStatus]);

  const counts = useMemo(
    () => ({
      all: enhancedVehicles.length,
      active: enhancedVehicles.filter((v) => v.displayStatus === 'active').length,
      inactive: enhancedVehicles.filter((v) => v.displayStatus === 'inactive').length,
    }),
    [enhancedVehicles]
  );

  const filteredVehicles = useMemo(() => {
    const term = searchValue.trim().toLowerCase();

    return enhancedVehicles.filter((v) => {
      if (statusFilter !== 'all' && v.displayStatus !== statusFilter) return false;
      if (!term) return true;
      return (
        v.immatriculation.toLowerCase().includes(term) ||
        (v.brand ?? '').toLowerCase().includes(term) ||
        (v.model ?? '').toLowerCase().includes(term)
      );
    });
  }, [enhancedVehicles, searchValue, statusFilter]);

  // Derived from the live list rather than held in state, so a selected
  // vehicle's speed and position keep updating instead of freezing at the
  // values it had when it was clicked.
  const selectedVehicle = useMemo(
    () => filteredVehicles.find((v) => v.vin === selectedVin) ?? null,
    [filteredVehicles, selectedVin]
  );

  const handleConnect = useCallback(async () => {
    if (isConnecting || isConnected) return;
    setIsConnecting(true);
    try {
      await connect();
      setFailedAttempts(0);
    } catch {
      setFailedAttempts((n) => n + 1);
    } finally {
      setIsConnecting(false);
    }
  }, [connect, isConnected, isConnecting]);

  // ── Map ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapRef.current || map) return;

    const instance = L.map(mapRef.current, {
      center: [46.603354, 1.888334], // France
      zoom: 6,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(instance);

    setMap(instance);
  }, [map]);

  /*
   * Teardown.
   *
   * Split from the mount effect and given real dependencies. The previous
   * cleanup ran from an effect with an empty dependency array, so its closure
   * captured `map` at its initial value — null. `map?.remove()` was therefore
   * always a no-op: the Leaflet instance, its tile requests and its DOM
   * survived unmount, and navigating back threw "Map container is already
   * initialized".
   */
  useEffect(() => {
    if (!map) return;
    return () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current.clear();
      map.remove();
    };
  }, [map]);

  useEffect(() => {
    return () => {
      if (disconnect) disconnect();
    };
  }, [disconnect]);

  const createVehicleIcon = useCallback(
    (status: 'active' | 'inactive', selected: boolean) => {
      const color = status === 'active' ? '#22c55e' : '#ef4444';
      const r = selected ? 13 : 10;
      return L.divIcon({
        html: `<svg width="34" height="34" viewBox="0 0 34 34" xmlns="http://www.w3.org/2000/svg">
          ${selected ? `<circle cx="17" cy="17" r="16" fill="${color}" opacity="0.22"/>` : ''}
          <circle cx="17" cy="17" r="${r}" fill="${color}" stroke="#fff" stroke-width="3"/>
          <circle cx="17" cy="17" r="3.5" fill="#fff"/>
        </svg>`,
        className: '',
        iconSize: [34, 34],
        iconAnchor: [17, 17],
        popupAnchor: [0, -17],
      });
    },
    []
  );

  const buildPopup = useCallback(
    (v: EnhancedVehicle) => {
      const row = (label: string, value: string) =>
        `<div style="display:flex;justify-content:space-between;gap:16px;margin:3px 0;font-size:13px;">
           <span style="color:#6b7280;">${escapeHtml(label)}</span>
           <span style="color:#111827;font-weight:600;">${escapeHtml(value)}</span>
         </div>`;

      return `<div style="min-width:220px;font-family:inherit;">
        <div style="font-weight:700;font-size:15px;margin-bottom:6px;">${escapeHtml(v.immatriculation)}</div>
        <div style="color:#6b7280;font-size:12px;margin-bottom:8px;">${escapeHtml(`${v.brand ?? ''} ${v.model ?? ''}`.trim())}</div>
        ${row(t('geolocation.speed', 'Vitesse'), `${v.speed.toFixed(1)} km/h`)}
        ${row(t('geolocation.ignition', 'Allumage'), v.ignition ? 'ON' : 'OFF')}
        ${v.fuelLevel != null ? row(t('geolocation.fuelLevel', 'Carburant'), `${v.fuelLevel}%`) : ''}
        ${v.batteryLevel != null ? row(t('geolocation.batteryLevel', 'Batterie'), `${v.batteryLevel}%`) : ''}
        <div style="margin-top:8px;color:#9ca3af;font-size:11px;">${escapeHtml(new Date(v.timestamp).toLocaleString('fr-FR'))}</div>
      </div>`;
    },
    [t]
  );

  useEffect(() => {
    if (!map) return;

    const currentVins = new Set(filteredVehicles.map((v) => v.vin));
    const markers = markersRef.current;

    for (const [vin, marker] of markers) {
      if (!currentVins.has(vin)) {
        marker.remove();
        markers.delete(vin);
      }
    }

    for (const vehicle of filteredVehicles) {
      const isSelected = vehicle.vin === selectedVin;
      const existing = markers.get(vehicle.vin);

      if (existing) {
        existing.setLatLng([vehicle.latitude, vehicle.longitude]);
        existing.setIcon(createVehicleIcon(vehicle.displayStatus, isSelected));
        existing.setPopupContent(buildPopup(vehicle));
      } else {
        const marker = L.marker([vehicle.latitude, vehicle.longitude], {
          icon: createVehicleIcon(vehicle.displayStatus, isSelected),
          title: vehicle.immatriculation,
        })
          .addTo(map)
          .bindPopup(buildPopup(vehicle), { maxWidth: 300 });

        marker.on('click', () => setSelectedVin(vehicle.vin));
        markers.set(vehicle.vin, marker);
      }
    }
  }, [map, filteredVehicles, selectedVin, createVehicleIcon, buildPopup]);

  const centerOnAll = useCallback(() => {
    if (!map || filteredVehicles.length === 0) return;
    map.fitBounds(
      L.latLngBounds(filteredVehicles.map((v) => [v.latitude, v.longitude] as L.LatLngTuple)),
      { padding: [48, 48] }
    );
  }, [map, filteredVehicles]);

  /*
   * Recentrage automatique la première fois que des véhicules apparaissent.
   *
   * La carte s'initialise sur la France au zoom 6 : un véhicule unique y est un
   * point de 34 px introuvable à l'œil, et l'opérateur concluait que rien ne
   * remontait. Une seule fois — après quoi la vue appartient à l'utilisateur,
   * et un recentrage à chaque push arracherait la carte sous son curseur.
   */
  const hasAutoFittedRef = useRef(false);

  useEffect(() => {
    if (hasAutoFittedRef.current) return;
    if (!map || filteredVehicles.length === 0) return;
    hasAutoFittedRef.current = true;

    if (filteredVehicles.length === 1) {
      const [only] = filteredVehicles;
      map.setView([only.latitude, only.longitude], 14);
      return;
    }

    centerOnAll();
  }, [map, filteredVehicles, centerOnAll]);

  const selectVehicle = useCallback(
    (v: EnhancedVehicle) => {
      setSelectedVin(v.vin);
      map?.setView([v.latitude, v.longitude], 15);
    },
    [map]
  );

  const connectionLabel = isConnecting
    ? t('geolocation.connecting', 'Connexion…')
    : isConnected
    ? t('geolocation.connected', 'Connecté')
    : failedAttempts >= maxReconnectAttempts
    ? t('geolocation.connectionFailed', 'Échec de connexion')
    : t('geolocation.disconnected', 'Déconnecté');

  const filters: Array<{ id: StatusFilter; label: string; count: number; dot?: string }> = [
    { id: 'all', label: t('geolocation.all', 'Tous'), count: counts.all },
    { id: 'active', label: t('geolocation.active', 'Actif'), count: counts.active, dot: '#22c55e' },
    { id: 'inactive', label: t('geolocation.inactive', 'Inactif'), count: counts.inactive, dot: '#ef4444' },
  ];

  return (
    <Sidebar
      styles={{
        sidebarRightRoot: classes.geolocationRoot,
        sidebarRoot: classes.contentMain,
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className={classes.header}>
        <div className={classes.headerTitleSection}>
          <CustomTypography
            text={t('geolocation.title', 'Géolocalisation')}
            variant="h2"
            color={colors.darkblack}
          />
          <div className={classes.connectionStatus}>
            <span
              className={classNames(classes.connectionDot, {
                [classes.connected]: isConnected,
                [classes.disconnected]: !isConnected,
              })}
            />
            <CustomTypography
              text={connectionLabel}
              variant="link"
              color={isConnected ? colors.success : colors.error}
              fontWeight="600"
            />
          </div>
        </div>

        <div className={classes.headerButtons}>
          {/* Reconnect is only meaningful while the socket is down. Shown
              permanently, it invited people to "refresh" a live stream that
              already updates itself. */}
          {!isConnected && (
            <CustomButton
              label={t('geolocation.reconnect', 'Reconnecter')}
              variant="secondary"
              onClick={handleConnect}
              disabled={isConnecting}
            />
          )}
          <CustomButton
            label={t('geolocation.centerAll', 'Centrer sur tous')}
            variant="primary"
            onClick={centerOnAll}
            disabled={filteredVehicles.length === 0}
          />
        </div>
      </div>

      {alerts.length > 0 && (
        <div className={classes.alertsContainer}>
          <CustomTypography
            text={t('geolocation.alerts', {
              // Interpolated, not baked into the default string. Passing the
              // count inside the fallback means the number only ever appears
              // when NO translation exists — add one and the alert silently
              // loses the figure that makes it useful.
              defaultValue: '{{count}} alerte(s) active(s)',
              count: alerts.length,
            })}
            variant="link"
            color={colors.secondary}
            fontWeight="600"
          />
        </div>
      )}

      {/*
        Map and list side by side, in one viewport-height row.

        They were stacked before: a full-height map with the vehicle list
        underneath it, so reaching the list meant scrolling the map off screen
        — and selecting a vehicle unmounted the list entirely, leaving no way
        back except closing the panel. Operators need to scan the fleet and see
        where it is at the same time, which is the whole point of this screen.
      */}
      <div className={classes.workspace}>
        <aside className={classes.rail}>
          <div className={classes.railSearch}>
            <input
              className={classes.searchInput}
              type="search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder={t('geolocation.searchPlaceholder', 'Immatriculation, marque, modèle…')}
              aria-label={t('geolocation.searchLabel', 'Rechercher un véhicule')}
            />

            {/*
              The legend is now the filter. It previously sat beside the search
              box explaining what two colours meant and did nothing; the same
              row now carries the counts and filters the list and the map
              together.
            */}
            <div className={classes.filterRow} role="tablist">
              {filters.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  role="tab"
                  aria-selected={statusFilter === f.id}
                  className={classNames(classes.filterChip, {
                    [classes.filterChipActive]: statusFilter === f.id,
                  })}
                  onClick={() => setStatusFilter(f.id)}
                >
                  {f.dot && (
                    <span className={classes.legendDot} style={{ backgroundColor: f.dot }} />
                  )}
                  {f.label}
                  <span className={classes.filterCount}>{f.count}</span>
                </button>
              ))}
            </div>
          </div>

          <div className={classes.railList}>
            {filteredVehicles.length === 0 ? (
              <div className={classes.emptyState}>
                <CustomTypography
                  text={
                    !isConnected
                      ? t('geolocation.disconnectedHint', 'Flux temps réel indisponible.')
                      : counts.all === 0
                      ? t('geolocation.noVehiclesTracked', 'Aucun véhicule ne remonte de position.')
                      : t('geolocation.noMatch', 'Aucun véhicule ne correspond aux filtres.')
                  }
                  variant="link"
                  color="#9ca3af"
                />
                {counts.all > 0 && (
                  <CustomButton
                    label={t('geolocation.resetFilters', 'Réinitialiser')}
                    variant="secondary"
                    onClick={() => {
                      setSearchValue('');
                      setStatusFilter('all');
                    }}
                  />
                )}
              </div>
            ) : (
              filteredVehicles.map((v) => (
                <button
                  key={v.vin}
                  type="button"
                  className={classNames(classes.vehicleRow, {
                    [classes.vehicleRowSelected]: v.vin === selectedVin,
                  })}
                  onClick={() => selectVehicle(v)}
                >
                  <span
                    className={classes.vehicleStatusDot}
                    style={{
                      backgroundColor: v.displayStatus === 'active' ? '#22c55e' : '#ef4444',
                    }}
                  />
                  <span className={classes.vehicleRowBody}>
                    <span className={classes.vehicleRowTitle}>{v.immatriculation}</span>
                    <span className={classes.vehicleRowSub}>
                      {`${v.brand ?? ''} ${v.model ?? ''}`.trim() || '—'}
                    </span>
                  </span>
                  <span className={classes.vehicleRowSpeed}>{v.speed.toFixed(0)} km/h</span>
                </button>
              ))
            )}
          </div>

          {/* Details replace the list in place instead of unmounting it, and
              only while a vehicle is selected. */}
          {selectedVehicle && (
            <div className={classes.detailPanel}>
              <div className={classes.detailHeader}>
                <span className={classes.vehicleRowTitle}>
                  {selectedVehicle.immatriculation}
                </span>
                <button
                  type="button"
                  className={classes.detailClose}
                  onClick={() => setSelectedVin(null)}
                  aria-label={t('common.close', 'Fermer')}
                >
                  ×
                </button>
              </div>

              <dl className={classes.detailGrid}>
                {[
                  { k: t('geolocation.speed', 'Vitesse'), v: `${selectedVehicle.speed.toFixed(1)} km/h` },
                  { k: t('geolocation.ignition', 'Allumage'), v: selectedVehicle.ignition ? 'ON' : 'OFF' },
                  { k: t('geolocation.heading', 'Direction'), v: `${selectedVehicle.heading}°` },
                  ...(selectedVehicle.odometer
                    ? [{ k: t('geolocation.odometer', 'Kilométrage'), v: `${selectedVehicle.odometer.toLocaleString()} km` }]
                    : []),
                  ...(selectedVehicle.fuelLevel != null
                    ? [{ k: t('geolocation.fuelLevel', 'Carburant'), v: `${selectedVehicle.fuelLevel}%`, c: getFuelColor(selectedVehicle.fuelLevel) }]
                    : []),
                  ...(selectedVehicle.batteryLevel != null
                    ? [{ k: t('geolocation.batteryLevel', 'Batterie'), v: `${selectedVehicle.batteryLevel}%`, c: getBatteryColor(selectedVehicle.batteryLevel) }]
                    : []),
                ].map((row: any) => (
                  <React.Fragment key={row.k}>
                    <dt className={classes.detailKey}>{row.k}</dt>
                    <dd className={classes.detailValue} style={row.c ? { color: row.c } : undefined}>
                      {row.v}
                    </dd>
                  </React.Fragment>
                ))}
              </dl>

              <div className={classes.detailFooter}>
                {t('geolocation.lastUpdate', 'Dernière position')} :{' '}
                {new Date(selectedVehicle.timestamp).toLocaleString('fr-FR')}
              </div>

              {/*
                "Voir l'historique" and "Créer une alerte" used to sit here and
                only console.log. A button that looks live and does nothing is
                worse than an absent feature: it reads as a broken product.
                They come back when the screens behind them exist.
              */}
            </div>
          )}
        </aside>

        <div className={classes.mapContainer}>
          <div ref={mapRef} className={classes.mapElement} />

          {/*
            The map is not silent when it has nothing to show. Before, zero
            vehicles rendered an empty view of Europe with no explanation, and
            a disconnected socket looked identical to a fleet that was simply
            parked.
          */}
          {filteredVehicles.length === 0 && (
            <div className={classes.mapEmptyOverlay}>
              <CustomTypography
                text={
                  !isConnected
                    ? t('geolocation.disconnectedHint', 'Flux temps réel indisponible.')
                    : counts.all === 0
                    ? t('geolocation.noVehiclesTracked', 'Aucun véhicule ne remonte de position.')
                    : t('geolocation.noMatch', 'Aucun véhicule ne correspond aux filtres.')
                }
                variant="link"
                color={colors.darkblack}
                fontWeight="600"
              />
            </div>
          )}
        </div>
      </div>
    </Sidebar>
  );
};
